// #region imports
import { call, put, select } from 'redux-saga/effects';
import { RootState } from '../../../../core/redux/store';
import { SheetManagerHistory } from '../../../types';
import sessionStorageSingleton from '../../../../core/configs/sessionStorageSingleton';
import { UserKeyStore } from '../../../../core/database/schemas';
import {
  disableManualSave,
  manualModeChangesDiscarded,
  manualModeChangesSaved,
} from '../../sheetsManagerSlice';
import {
  startSaveSheetRootChanges,
  startSaveSheetChanges,
  startSaveUserGroupsChanges,
  startSaveUserSheetHistory,
  startSaveSheetsIdToNameChanges,
} from '../../../utils/storage/storage';
import {
  discardManualModeChanges,
  saveManualModeChanges,
} from './manualSaveModeActions';
import {
  ResetSheetsWithChangesResult,
  resetSheetsWithChanges,
} from './manualModeHelpers';
import { tabSharedWorker } from '../../../../core/sharedWorker/sharedWorkerManager';
import { SharedWorkerMessage } from '../../../../core/sharedWorker/types';
import { ManualModeChangesDiscarded } from '../../sheetsManagerSliceHelpers';
import { saveManualModeToggle } from '../../../../core/utils/auth/authUtils';
// #endregion

// #region save manual mode changes
export function* startSaveManualModeChanges({
  payload: disable,
}: ReturnType<typeof saveManualModeChanges>) {
  const state: RootState = yield select();
  const sheetsManagerState = state.sheetsManager;
  const userId = state.auth.user.id;
  const unSavedChanges = state.sheetsManager.unSavedChanges;
  const unSavedHistory = state.sheetsManager.unSavedHistory;

  const userFeKey: CryptoKey | undefined =
    yield sessionStorageSingleton.getFeKey(UserKeyStore.UserFeKey);
  if (!userFeKey) return;

  console.log('unSavedChanges', unSavedChanges);
  console.log('unSavedHistory', unSavedHistory);

  for (let changes of unSavedChanges.sheetsIdToName) {
    yield call(startSaveSheetsIdToNameChanges, changes, true);
  }

  for (let changes of unSavedChanges.sheetRoot) {
    yield call(startSaveSheetRootChanges, changes, true);
  }

  const sheetIds = Object.keys(unSavedChanges.sheets);
  for (let sheetId of sheetIds) {
    const sheetChanges = unSavedChanges.sheets[sheetId];
    if (sheetChanges) {
      for (let changes of sheetChanges) {
        yield call(startSaveSheetChanges, sheetId, changes, true);
      }
    }
  }

  for (let history of unSavedHistory) {
    yield call(startSaveUserSheetHistory, userId, userFeKey, history, true);
  }

  for (let changes of unSavedChanges.groups) {
    yield call(startSaveUserGroupsChanges, changes, true);
  }

  // const updatedSheets: Sheets = yield recompileStaleSheets(
  //   sheetsManagerState.staleSheets
  // );

  yield put(
    manualModeChangesSaved({
      sheets: {},
      // sheets: updatedSheets,
      disable,
    })
  );

  tabSharedWorker.port.postMessage({
    type: SharedWorkerMessage.SyncManualModeAfterChangesSaved,
    userId: userId,
    params: {
      disable: !!disable,
    },
  });
  if (disable) saveManualModeToggle(undefined);
}
// #endregion

// #region discard manual mode changes
export function* startDiscardManualModeChanges({
  payload: disable,
}: ReturnType<typeof discardManualModeChanges>) {
  const state: RootState = yield select();
  const userId = state.auth.user.id;
  const primaryStorage = state.auth.app.primaryStorage;
  const unSavedChanges = state.sheetsManager.unSavedChanges;
  const unSavedHistory = state.sheetsManager.unSavedHistory;
  // const unSavedChangesCount = state.sheetsManager.unSavedChangesCount;
  const sheets = state.sheetsManager.sheets;
  // const groups = state.sheetsManager.groups;

  const userFeKey: CryptoKey | undefined =
    yield sessionStorageSingleton.getFeKey(UserKeyStore.UserFeKey);
  const directoryHandler: FileSystemDirectoryHandle | undefined =
    yield sessionStorageSingleton.getDirectoryHandler();
  if (!userFeKey) return;

  // const { unSavedChanges }: ReadUnSavedSheetChangesResult = yield call(
  //   readUnSavedSheetChanges,
  //   userId,
  //   userFeKey,
  //   primaryStorage,
  //   directoryHandler
  // );

  // console.log('unSavedChanges', unSavedChanges);

  // if (disable) {
  //   tabSharedWorker.port.postMessage({
  //     type: SharedWorkerMessage.SyncManualSaveToggle,
  //     userId,
  //     isManualSave: false,
  //   });
  // }
  if (disable) saveManualModeToggle(undefined);

  if (!unSavedHistory.length) {
    if (disable) {
      yield put(disableManualSave());
      tabSharedWorker.port.postMessage({
        type: SharedWorkerMessage.SyncManualSaveToggle,
        userId,
        isManualSave: false,
      });
    }

    return;
  }

  // On Discard Flush History
  let sheetsHistory: SheetManagerHistory = {
    past: [],
    future: [],
  };

  // yield startClearUserSheetHistory(userId);
  // const historyResult: ResultWithOptionalError<SheetManagerHistory> =
  //   yield loadUserSheetHistory(userId, userFeKey);
  // if (historyResult.data) {
  //   sheetsHistory = historyResult.data;
  // }

  // const groupsAndSheetNamesResult: ResultWithOptionalError<ReadGroupsAndSheetsIdToNameResult> =
  //   yield readGroupsAndSheetsIdToName(userId, primaryStorage);
  // if (groupsAndSheetNamesResult.error || !groupsAndSheetNamesResult.data)
  //   return showErrorToast('Error reading groups and sheetsIdToName');

  const {
    updatedSheets,
    updatedSheetViews,
    sheetsToRemoveFromMem,
  }: ResetSheetsWithChangesResult = yield resetSheetsWithChanges(
    sheets,
    unSavedChanges
  );

  const params: ManualModeChangesDiscarded = {
    updatedGroups: {}, // TODO: Remove this
    updatedSheetsIdToName: {}, // TODO: Remove this
    // updatedGroups: groupsAndSheetNamesResult.data.groups,
    // updatedSheetsIdToName: groupsAndSheetNamesResult.data.sheetsIdToName,
    history: sheetsHistory,
    disable,
    updatedSheets,
    sheetsToRemoveFromMem,
    updatedSheetViews,
  };
  yield put(manualModeChangesDiscarded(params));

  tabSharedWorker.port.postMessage({
    type: SharedWorkerMessage.SyncManualModeAfterChangesDiscard,
    userId: userId,
    params,
  });
}
// #endregion
